import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Newspaper, X, ArrowRight } from "lucide-react";

/**
 * Announcement banner that sits at the very top of every page.
 * Not sticky — scrolls away with content. Dismissible (remembered in
 * localStorage so it stays dismissed across navigations).
 *
 * Rotates through ANNOUNCEMENTS on an interval when there's more than
 * one. Add an entry to show a new message alongside existing ones;
 * bump DISMISS_KEY_VERSION whenever the set changes so previously
 * dismissed users see the update.
 */
type Announcement = {
  id: string;
  path: string;
  icon: typeof ShieldCheck;
  render: () => JSX.Element;
};

const ANNOUNCEMENTS: Announcement[] = [
  {
    id: "cfo-revenue-software-2026-10",
    path: "/blog/how-cfos-evaluate-revenue-software-ai-agents",
    icon: Newspaper,
    render: () => (
      <span className="text-center">
        New:{" "}
        <span className="font-semibold text-primary underline-offset-4 group-hover:underline">
          How CFOs should evaluate revenue software
        </span>
        <span className="hidden sm:inline">
          <span className="text-muted-foreground"> · </span>Read the six questions
        </span>
      </span>
    ),
  },
  {
    id: "soc2-audit-2026-07",
    path: "/blog/the-audit-was-us-going-first",
    icon: ShieldCheck,
    render: () => (
      <span className="text-center">
        We are{" "}
        <span className="font-semibold text-primary underline-offset-4 group-hover:underline">
          SOC 2 certified
        </span>
        <span className="text-muted-foreground"> · </span>Learn more
      </span>
    ),
  },
];

const DISMISS_KEY_VERSION = ANNOUNCEMENTS.map((a) => a.id).join("+");
const DISMISS_KEY = `meshly_banner_dismissed_${DISMISS_KEY_VERSION}`;
const ROTATE_MS = 7000;

export const AnnouncementBanner = () => {
  const [visible, setVisible] = useState(false);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const reducedMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    []
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const dismissed = window.localStorage.getItem(DISMISS_KEY) === "1";
    setVisible(!dismissed);
  }, []);

  useEffect(() => {
    if (ANNOUNCEMENTS.length < 2 || reducedMotion || paused || !visible) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % ANNOUNCEMENTS.length);
    }, ROTATE_MS);
    return () => clearInterval(timer);
  }, [reducedMotion, paused, visible]);

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setVisible(false);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(DISMISS_KEY, "1");
    }
  };

  if (!visible) return null;

  const current = ANNOUNCEMENTS[index];
  const Icon = current.icon;

  return (
    <div
      className="relative w-full bg-gradient-to-r from-slate-50 via-white to-slate-50 border-b border-slate-200/80"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <Link
        to={current.path}
        key={current.id}
        className="group block animate-in fade-in slide-in-from-bottom-1 duration-500"
      >
        <div className="container mx-auto flex items-center justify-center gap-2 px-12 py-3 text-base text-foreground">
          <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          {current.render()}
          <ArrowRight className="h-4 w-4 shrink-0 text-primary transition-transform group-hover:translate-x-0.5" />
        </div>
      </Link>

      {ANNOUNCEMENTS.length > 1 && (
        <div className="absolute right-11 top-1/2 flex -translate-y-1/2 items-center gap-1.5">
          {ANNOUNCEMENTS.map((a, i) => (
            <button
              key={a.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIndex(i);
              }}
              aria-label={`Show announcement ${i + 1} of ${ANNOUNCEMENTS.length}`}
              aria-current={i === index}
              className={`h-1.5 w-1.5 rounded-full transition-colors ${
                i === index ? "bg-primary" : "bg-slate-300 hover:bg-slate-400"
              }`}
            />
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={handleDismiss}
        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground transition-colors hover:bg-slate-200/60 hover:text-foreground"
        aria-label="Dismiss announcement"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default AnnouncementBanner;
